import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetVehicleQuery, useUpdateVehicleMutation} from '@dms/api/vehicle';
import {useCreateServiceVehicleMutation} from '@dms/api/workshop';
import {
  ServiceVehicle,
  VehicleForm as VFKit,
  ServiceVehicleValidationSchema,
  useAdditionalNote,
  toAPIVehicle,
  useGetCarFeatures,
  useFormNavigate,
  toBaseVehicle,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  useVehicleDocumentSync,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {formatDocumentTitle, handleApiError} from '@dms/shared';

import {noop, useRequiredParams} from 'shared/web';

import {ServiceVehicleForm} from '../components/ServiceVehicleForm';

export function CreateServiceFromSaleVehicle() {
  const {navigateOnDiscard, navigateOnConfirm} = useFormNavigate();
  const [updateVehicleMutation, {isLoading: isVehicleUpdating}] = useUpdateVehicleMutation();

  const {id: vehicleId} = useRequiredParams();

  // const {
  //   generateSchema,
  //   isMandatoryFieldsEnabled,
  //   isLoading: isMandatoryFieldsLoading,
  // } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const [createServiceVehicle, {isLoading: isServiceVehicleCreating}] =
    useCreateServiceVehicleMutation();
  const {handleSaveNote} = useAdditionalNote();
  const [getCarFeatures] = useGetCarFeatures();
  const {syncDocuments} = useVehicleDocumentSync();

  // const [formSchema, setFormSchema] = useState<ZodSchema<ServiceVehicle>>(() =>
  //   generateSchema(ServiceVehicleValidationSchema, 'VEHICLETYPE_PASSENGER_CAR')
  // );

  // const handleVehicleTypeChange = useCallback(
  //   (type: VehicleTypeEnum) => {
  //     const newSchema = generateSchema(ServiceVehicleValidationSchema, type);
  //     setFormSchema(newSchema);
  //   },
  //   [generateSchema]
  // );

  const handleSave = async (values: ServiceVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    return (
      updateVehicleMutation({
        vehicleId,
        updateVehicleRequestBody: apiPayload,
        validateMandatoryFields: false,
      })
        .unwrap()
        // Create service vehicle
        .then(async () => {
          await createServiceVehicle({vehicleId}).unwrap();
        })
        // Handle additional note
        .then(async () => {
          if (values.additionalNote) {
            await handleSaveNote(vehicleId, values.additionalNote);
          }
        })
        // Sync battery certificate document
        .then(async () => {
          await syncDocuments({
            vehicleId: vehicleId,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
        })
        // Navigate to vehicle detail
        .then(() => {
          navigateOnConfirm(vehicleId);
        })
        .catch(handleApiError)
    );
  };

  const isCreating = isVehicleUpdating || isServiceVehicleCreating;
  const isLoading = /* isMandatoryFieldsLoading || */ isVehicleLoading;
  const isError = isVehicleError;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.actions.createServiceForSaleVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus isLoading={isLoading} isError={isError}>
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={ServiceVehicleValidationSchema}
          defaultValues={{
            ...toBaseVehicle(vehicle!),
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
          }}
        >
          <ServiceVehicleForm
            onSave={handleSave}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating}
            onVehicleTypeChange={noop}
            modalKeys={{
              headline: i18n.t('entity.vehicle.actions.createServiceForSaleVehicle'),
            }}
            tabRoutePattern="createServiceFromSaleVehicleV2Tab"
            disableVinDecoder
            infoAlert={{
              title: i18n.t('entity.vehicle.labels.creatingServiceForSaleVehicleMessage'),
              message: i18n.t('entity.vehicle.labels.creatingServiceForSaleVehicle'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
